import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import {
  Table,
  Rate,
  Tag,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Typography,
  Result,
  Tooltip,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n} star${n === 1 ? '' : 's'}` }));

const FacilityRatingsPage = () => {
  const { hasAnyPermission } = usePermissions();
  const allowed = useMemo(
    () => hasAnyPermission(['admin.*', 'facility_ratings.manage_all']),
    [hasAnyPermission],
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState({ facility_id: undefined, score: undefined, range: null });

  const load = useCallback(async (page = pagination.page, limit = pagination.limit) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.facility_id) params.facility_id = filters.facility_id;
      if (filters.score) params.score = filters.score;
      if (filters.range?.[0]) params.from = filters.range[0].format('YYYY-MM-DD 00:00:00');
      if (filters.range?.[1]) params.to = filters.range[1].format('YYYY-MM-DD 23:59:59');

      const resp = await lib.getFacilityRatings(params);
      if (resp?.data?.error) return;
      setRows(resp?.data?.data || []);
      setPagination((prev) => ({ ...prev, ...(resp?.data?.pagination || {}), page, limit }));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (allowed) load(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, filters]);

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Admin only"
        subTitle="Facility ratings moderation requires admin.* or facility_ratings.manage_all."
      />
    );
  }

  const columns = [
    {
      title: 'Facility',
      dataIndex: 'facility_name',
      key: 'facility_name',
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: 160,
      render: (score) => <Rate disabled value={score} style={{ fontSize: 14 }} />,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => (
        <Space size={[4, 4]} wrap>
          {(tags || []).map((t) => <Tag key={t}>{t}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      render: (comment) => comment
        ? <Tooltip title={comment}><Text ellipsis style={{ maxWidth: 240, display: 'inline-block' }}>{comment}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      // Who rated. Today only the principal's account can log in, so the
      // policy number is the truthful rater identity; the row's insurance_no
      // (the visit's enrollee) rides along in the API for when enrollee login
      // ships and individual raters become nameable.
      title: 'Policy No.',
      dataIndex: 'policy_no',
      key: 'policy_no',
      width: 140,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 110,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v) => v ? dayjs(v).format('MMM D, YYYY HH:mm') : '—',
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0 }}>Facility ratings</Title>
      <ModuleNav items={MODULE_NAVS.facilities} />
      <Paragraph type="secondary">
        Individual feedback submitted by members after a completed encounter or appointment.
      </Paragraph>

      <Space wrap style={{ marginBottom: 16 }}>
        <InputNumber
          placeholder="Facility ID"
          min={1}
          value={filters.facility_id}
          onChange={(v) => setFilters((f) => ({ ...f, facility_id: v || undefined }))}
        />
        <Select
          allowClear
          placeholder="Score"
          style={{ width: 140 }}
          options={SCORE_OPTIONS}
          value={filters.score}
          onChange={(v) => setFilters((f) => ({ ...f, score: v }))}
        />
        <RangePicker
          value={filters.range}
          onChange={(range) => setFilters((f) => ({ ...f, range }))}
        />
        <Button icon={<ReloadOutlined />} onClick={() => load(pagination.page, pagination.limit)} loading={loading}>
          Reload
        </Button>
      </Space>

      <Table
        rowKey={(row) => `${row.source}:${row.visit_id}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, limit) => load(page, limit),
        }}
      />
    </div>
  );
};

export default FacilityRatingsPage;
